/**
 * UsageBillingPage — the tenant's own billing view. Shows ONLY the marked-up
 * amount we charge them (their statement) — never our true cost or the markup
 * rate (that stays operator-side). Admin-gated by the API (/api/costs/statement
 * returns 403 to non-admins). Built on the v3 design system.
 */
import { useEffect, useState } from 'react';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';

async function authFetch(url) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}` } });
  if (res.status === 401) {
    try { localStorage.removeItem('mickey_token'); } catch {}
    if (typeof window !== 'undefined' && !/\/login$/.test(window.location.pathname)) window.location.href = '/login';
  }
  return res;
}

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function UsageBillingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/costs/statement');
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 403) { setError('Usage & Billing is available to admins.'); return; }
        if (!res.ok) { setError(body.error || 'Could not load your usage.'); return; }
        setData(body);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Something went wrong.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const over = data && !data.within_allotment;
  const pct = data ? Math.min(100, Number(data.percent_used) || 0) : 0;
  // Shown when there are unpriced calls, and when their count could not be read (calls null).
  const showUnpriced = !!(data && data.unpriced && data.unpriced.calls !== 0 && data.unpriced.note);

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Usage &amp; Billing</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Your AI usage this month and what it's billed at.</p>

      {loading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}
      {error && <Card className="p-6"><p className="text-sm text-gray-600 dark:text-gray-300">{error}</p></Card>}

      {data && (
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">This month's charge</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{usd(data.charge)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 mb-1">of {usd(data.allotment)} included</p>
                <Badge variant={over ? 'warning' : 'success'}>{over ? 'Over your plan' : 'Within your plan'}</Badge>
              </div>
            </div>
            <div className="mt-4">
              <div className="h-2 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden">
                <div className={`h-full rounded-full ${over ? 'bg-warning' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between mt-1 text-[11px] text-gray-400">
                <span>{pct.toFixed(0)}% of plan used</span>
                <span>Projected end of month: {usd(data.projected_charge)}</span>
              </div>
            </div>
            {over && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                You're past your included plan; usage beyond it is billed at your overage rate.
              </p>
            )}
          </Card>

          {((Array.isArray(data.by_feature) && data.by_feature.length > 0) || showUnpriced) && (
            <Card className="p-5">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-3">What you're using</p>
              <div className="space-y-2">
                {(data.by_feature || []).map((f) => (
                  <div key={f.feature} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-200">{prettyFeature(f.feature)}</span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {f.calls} call{f.calls === 1 ? '' : 's'} · <span className="font-medium text-gray-700 dark:text-gray-200">{usd(f.charge)}</span>
                    </span>
                  </div>
                ))}
              </div>
              {/* Calls with no known cost yet: counted here, not in the charge above (never shown as $0). */}
              {showUnpriced && (
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{data.unpriced.note}</p>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function prettyFeature(k) {
  const map = {
    'feedback-intake': 'Smart feedback',
    'timecard-ask': 'Ask AI (timecards)',
    'persona-chat': 'Assistant chat',
    'voice-transcribe': 'Voice to text',
    kee: 'Knowledge extraction',
    roadmap: 'Roadmap AI',
    vision: 'Document vision',
    calibration: 'Recommendation tuning',
    training: 'Training',
    other: 'Other',
  };
  return map[k] || k;
}
